import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Swal from "sweetalert2";
import "../../../Styles/StickyStatusBar.css";
import "../../../Styles/ScheduleIngestionModal.css";
import LoadingScreen from "../../../ReusableBlocks/LoadingScreen";
import Switch from "../../../ReusableBlocks/Switch";
import { editYadsRecurringSchedule, patchYadsRecurringScheduleReset } from "../../../Redux/Dashboard/External/Yads/YadsEditRecurringAction";
import { getYadsPollingStatus } from "../../../Redux/Dashboard/External/Yads/GetPollingStatusAction";

/** ---------- small helpers ---------- */
const pad2 = (n) => String(n).padStart(2, "0");
const withIds = (scenarioGroups = []) =>
  (scenarioGroups || []).map((g) => ({
    ...g,
    scenarios: (g.scenarios || []).map((s) => ({
      ...s,
      id: s.id || s.title,
      runMode: s.runMode ?? null,
      count: s.count ?? 1,
      durationHours: s.durationHours ?? 1,
      selected: false,
    })),
  }));

const dateInputFromUtc = (isoUtc, tz) => {
  if (!isoUtc) return "";
  try {
    // force parse as UTC (backend doesn't suffix 'Z')
    const d = new Date(isoUtc.endsWith("Z") ? isoUtc : isoUtc + "Z");

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);

    const y = parts.find(p => p.type === "year")?.value;
    const m = parts.find(p => p.type === "month")?.value;
    const da = parts.find(p => p.type === "day")?.value;

    if (!y || !m || !da) return "";
    return `${y}-${m}-${da}`;   // HTML date input format
  } catch {
    return "";
  }
};

// 12h UI selections
const HOURS_12 = Array.from({ length: 12 }, (_, i) => pad2(i + 1));
const MINUTES = Array.from({ length: 12 }, (_, i) => pad2(i * 5));
const to24h = (h12, ampm) => {
  let h = parseInt(h12 || "0", 10);
  if (ampm === "PM" && h < 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return h;
};


/** ---------- main component ---------- */
const Yads_Recurring_Scheduled_Edit = ({ isOpen, onClose, record, onSubmit }) => {



  const dispatch = useDispatch();
  // store
  const { tenantOptions = [], scenarioGroups: rawGroups = [] } =
    useSelector((s) => s.YadsTenantScenario || {});
  const scenarioGroups = useMemo(() => withIds(rawGroups), [rawGroups]);

  const { loading: patchLoading, result: patchResult, error: patchError } =
    useSelector((s) => s.YadsEditRecurringSchedule || {});


  // put near your small helpers
  const norm = (s = "") => s.toLowerCase().replace(/[^a-z0-9]/g, "");


  // group inference helpers (same as one-time)
  const escape = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const cleanupReFor = (groupKey) => {
    // match "<group> Cleanup" or "<group>-Cleanup" etc. ignoring case/spaces/dashes
    const g = norm(groupKey);
    // build a regex that ignores non-alphanumerics between chars
    const spread = g.split("").map(ch => `${ch}[^a-z0-9]*`).join("");
    // ex: apexmobile -> a[^a-z0-9]*p[^a-z0-9]*e...
    return new RegExp(`^${spread}cleanup(?:$|[^a-z0-9])`, "i");
  };
  const titleToGroup = (title, groups) => {
    const nt = norm(title || "");
    const found = (groups || []).find(g => {
      const ng = norm(g.title || "");
      // accept “starts with” and strict equality on the normalized strings
      return nt.startsWith(ng) || nt === ng;
    });
    return found?.title || null;
  };


  const getScenarioRunMode = (scenario) => {
    const mode = String(scenario?.runMode ?? "").toLowerCase().trim();

    if (mode === "duration") return "duration";
    if (mode === "count") return "count";

    if (scenario?.durationHours != null) return "duration";

    return "count";
  };

  const isDurationScenario = (scenario) =>
    getScenarioRunMode(scenario) === "duration";

  const getScenarioValue = (scenario) => {
    if (isDurationScenario(scenario)) {
      return parseInt(scenario?.durationHours, 10) || 1;
    }
    return parseInt(scenario?.count, 10) || 1;
  };

  const buildScenarioLabel = (scenario) => {
    const value = getScenarioValue(scenario);
    return isDurationScenario(scenario)
      ? `${scenario.title} - ${value} hour${value > 1 ? "s" : ""}`
      : `${scenario.title} - ${value}`;
  };

  // Build saved selection per group → { [groupTitle]: { mode, items: [{title,id,count}] } }
  const preselectByGroup = useMemo(() => {
    if (!record || !Array.isArray(record.selectedScenarios)) return {};
    const map = {};

    record.selectedScenarios.forEach((sp) => {
      const title = sp?.title?.trim();
      if (!title) return;

      const gk = titleToGroup(title, scenarioGroups) || "Unknown";

      const catalogScenario =
        (scenarioGroups || [])
          .flatMap((g) => g.scenarios || [])
          .find((s) => s.title === sp.title);

      const id = catalogScenario?.id || sp.title;
      const type = sp?.type || null;

      const value =
        catalogScenario && isDurationScenario(catalogScenario)
          ? Number(sp.durationHours ?? 1)
          : Number(sp.count ?? 1);

      if (!map[gk]) {
        map[gk] = { mode: "Advanced", items: [], groupDefault: false };
      }

      map[gk].items.push({
        type,
        title: sp.title,
        id,
        count: value, // generic numeric value
      });

      if (type === "group" || norm(sp.title) === norm(gk)) {
        map[gk].groupDefault = true;
      }
    });

    Object.keys(map).forEach((gk) => {
      const items = map[gk].items || [];
      const reCleanup = cleanupReFor(gk);

      if (map[gk].groupDefault) {
        map[gk].mode = "Default";
        return;
      }

      if (items.length === 0) {
        map[gk].mode = "Default";
        return;
      }

      const allCleanup = items.every((it) => reCleanup.test(it.title));
      if (allCleanup) {
        map[gk].mode = "Reset";
        return;
      }

      const catalog = (scenarioGroups.find((g) => g.title === gk)?.scenarios || [])
        .map((s) => s.title);

      const nonCleanup = catalog.filter((t) => !reCleanup.test(t));
      const selectedNonCleanup = new Set(
        items.map((it) => it.title).filter((t) => !reCleanup.test(t))
      );

      map[gk].mode =
        nonCleanup.length > 0 && nonCleanup.every((t) => selectedNonCleanup.has(t))
          ? "Default"
          : "Advanced";
    });

    return map;
  }, [record, scenarioGroups]);



  // left/middle/right panes (same structure/feel as one-time editor)
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupScenarios, setSelectedGroupScenarios] = useState([]);
  const [groupCheckboxes, setGroupCheckboxes] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [scenarioModes, setScenarioModes] = useState({});
  const selectAllRef = React.useRef(null);
  // RTIC
  // null => Unset (don't send); true/false => send in payload
  const [resetRticCluster, setResetRticCluster] = useState(null);
  const [rticTouched, setRticTouched] = useState(false);

  // tenant form (prefill from store by tenantUrl)
  const [tenantForm, setTenantForm] = useState({
    tenantUrl: "",
    itsmUrl: "",
    key: "",
    secretKey: "",
    username: "",
    password: "",
  });

  // recurring-only fields
  const [ampm, setAmpm] = useState("AM");
  const [hourStr, setHourStr] = useState("");
  const [minuteStr, setMinuteStr] = useState("");
  const systemTimezone = React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    []
  );
  const [timezone, setTimezone] = useState(systemTimezone);
  const [enabled, setEnabled] = useState(true);
  const [endDateStr, setEndDateStr] = useState(""); // YYYY-MM-DD
  const [noEnd, setNoEnd] = useState(false); // YYYY-MM-DD or ""
  const [weekDays, setWeekDays] = useState([]); // ["Monday","Wednesday",...]

  const [isSaving, setIsSaving] = useState(false);

  /** ---------- helpers borrowed from your main page ---------- */
  const isInitTitle = (title = "") => /-Init$/i.test(title.trim());
  const isInitScenario = (s) => isInitTitle(s?.title);
  const escapeRegExp = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const cleanupMatcher = (groupKey) =>
    new RegExp(`^${escapeRegExp(groupKey)}(?:[\\s-]*)Cleanup(?:$|[\\s-])`, "i");

  const findGroupCleanups = (groupKey) => {
    const group = (scenarioGroups || []).find((g) => g?.title === groupKey);
    if (!group) return [];
    const re = cleanupMatcher(groupKey);
    return (group.scenarios || [])
      .map((s) => s.title?.trim())
      .filter(Boolean)
      .filter((title) => re.test(title));
  };




  const getCurrentMode = () =>
    selectedGroup ? scenarioModes[selectedGroup] || "Default" : "Default";

  const getAdvancedSelectionState = () => {
    if (!selectedGroupScenarios.length) return "none";
    const selectedCount = selectedGroupScenarios.filter((s) => s.selected).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === selectedGroupScenarios.length) return "all";
    return "some";
  };




  /** ---------- right-panel payload builder ---------- */
  const selectedItemsToScenariosPayload = (selectedItems, scenarioGroups) => {
    const scenarioSet = new Set();
    const scenariosPayload = [];

    const _cleanupMatcher = (groupKey) =>
      new RegExp(
        `^${groupKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:[\\s-]*)Cleanup(?:$|[\\s-])`,
        "i"
      );

    const _findGroupCleanups = (groupKey) => {
      const group = (scenarioGroups || []).find((g) => g?.title === groupKey);
      if (!group) return [];
      const re = _cleanupMatcher(groupKey);
      return (group.scenarios || [])
        .map((s) => s.title?.trim())
        .filter(Boolean)
        .filter((title) => re.test(title));
    };

    const deriveGroupFromItem = (item) => {
      if (item?.group) return item.group;
      if (typeof item?.id === "string") {
        const m = item.id.replace(/(?:\s|-)*Cleanup(?:-.*)?$/i, "").trim();
        if (m) return m;
      }
      if (typeof item?.label === "string") {
        const m = item.label.replace(/(?:\s|-)*Cleanup(?:-.*)?$/i, "").trim();
        if (m) return m;
      }
      return null;
    };

    selectedItems.forEach((item) => {
      // cleanup pseudo-tag → expand
      if (item?.type === "reset" || (typeof item?.id === "string" && item.id.startsWith("reset-"))) {
        const groupKey = deriveGroupFromItem(item);
        if (groupKey) {
          let titles = _findGroupCleanups(groupKey);
          if (!titles.length) {
            const allTitles = (Array.isArray(scenarioGroups) ? scenarioGroups : [])
              .flatMap((g) => (g.scenarios || []).map((s) => s.title?.trim()))
              .filter(Boolean);
            const re = _cleanupMatcher(groupKey);
            titles = allTitles.filter((t) => re.test(t));
          }
          titles.forEach((title) => {
            if (!scenarioSet.has(title)) {
              scenarioSet.add(title);
              scenariosPayload.push({ type: "scenario", title, count: 1 });
            }
          });
        }
        return;
      }

      // group tag
      if (item.id.startsWith("group-")) {
        scenariosPayload.push({ type: "group", title: item.label, count: 1 });
        return;
      }

      // scenario tag
      if (!scenarioSet.has(item.id)) {
        const scenario = (scenarioGroups || [])
          .flatMap((g) => g.scenarios || [])
          .find((s) => s.id === item.id || s.title === item.id);

        if (scenario) {
          scenarioSet.add(scenario.id);
          const parts = String(item.label).split(" - ");
          const parsedValue = parseInt(parts[parts.length - 1], 10) || 1;

          const payloadScenario = {
            type: "scenario",
            title: scenario.title,
          };

          if (isDurationScenario(scenario)) {
            payloadScenario.durationHours = parsedValue;
          } else {
            payloadScenario.count = parsedValue;
          }

          scenariosPayload.push(payloadScenario);
        }
      }
    });

    return scenariosPayload;
  };

  /** ---------- left pane: click a group (same pattern) ---------- */
  const restoreGroupScenarios = (group) => {
    const groupKey = group.title;

    // reset one-group state
    // HARD RESET like Yads_Ingestion (enforce one group at a time)
    setSelectedGroupScenarios([]);
    setSelectedItems([]);
    // setGroupScenarioSelectionMap({}); // ⬅️ remove this line — not defined in this component
    setGroupCheckboxes({});
    setScenarioModes({});

    setSelectedGroup(groupKey);


    const saved = preselectByGroup[groupKey];

    if (!saved) {
      // Default: all selected + one group tag
      const groupScenarios = (group.scenarios || []).map((s) => ({
        ...s,
        selected: true,
        count: s.count || 1,
      }));
      setSelectedGroupScenarios(groupScenarios);
      setSelectedItems([{ id: `group-${groupKey}`, group: groupKey, label: groupKey }]);
      setGroupCheckboxes({ [groupKey]: "checked" });
      setScenarioModes({ [groupKey]: "Default" });
      return;
    }

    const startMode = saved.mode || "Advanced";
    setScenarioModes({ [groupKey]: startMode });

    if (startMode === "Reset") {
      const cleanupTags = saved.items.map((it) => ({
        id: it.id,
        type: "scenario",
        group: groupKey,
        label: `${it.title} - ${it.count || 1}`,
      }));
      setSelectedItems(
        cleanupTags.length
          ? cleanupTags
          : [{ id: `reset-${groupKey}`, type: "reset", group: groupKey, label: `${groupKey} Cleanup` }]
      );
      setGroupCheckboxes({ [groupKey]: "none" });
      setSelectedGroupScenarios([]); // empty middle list in Reset
      return;
    }

    if (startMode === "Default") {
      const groupScenarios = (group.scenarios || []).map((s) => ({
        ...s,
        selected: true,
        count: s.count || 1,
        durationHours: s.durationHours || 1,
      }));
      setSelectedGroupScenarios(groupScenarios);
      setSelectedItems([{ id: `group-${groupKey}`, group: groupKey, label: groupKey }]);
      setGroupCheckboxes({ [groupKey]: "checked" });
      return;
    }

    // Advanced: mark saved items as selected with counts
    const byId = Object.fromEntries(saved.items.map((it) => [it.id, it.count || 1]));

    const updated = (group.scenarios || []).map((s) => {
      const savedValue = byId[s.id] ?? 1;

      return {
        ...s,
        selected: Object.prototype.hasOwnProperty.call(byId, s.id),
        count: isDurationScenario(s) ? (s.count || 1) : savedValue,
        durationHours: isDurationScenario(s) ? savedValue : (s.durationHours || 1),
      };
    });
    setSelectedGroupScenarios(updated);

    // Right pane: only this group's selected scenarios
    const tags = updated
      .filter((s) => s.selected)
      .map((s) => ({ id: s.id, group: groupKey, label: buildScenarioLabel(s) }));
    setSelectedItems(tags);

    const total = updated.length;
    const selectedCount = updated.filter((s) => s.selected).length;
    setGroupCheckboxes({
      [groupKey]: selectedCount === 0 ? "none" : selectedCount === total ? "checked" : "partial",
    });
  };


  /** ---------- middle pane: mode changes (same as one-time) ---------- */
  const handleScenarioModeChange = (mode, customGroupKey) => {
    const groupKey = customGroupKey || selectedGroup;
    if (!groupKey) return;

    // if leaving Reset, clear RTIC UI so it doesn't “stick”
    if (mode !== "Reset") {
      setResetRticCluster(null);
      setRticTouched(false);
    }

    setScenarioModes((prev) => ({ ...prev, [groupKey]: mode }));
    const groupObj = scenarioGroups.find((g) => g.title === groupKey);
    if (!groupObj) return;

    if (mode === "Default") {
      const groupScenarios = (groupObj.scenarios || []).map((s) => {
        const existing = selectedGroupScenarios.find((x) => x.id === s.id);

        return {
          ...s,
          selected: true,
          count: existing?.count || s.count || 1,
          durationHours: existing?.durationHours || s.durationHours || 1,
        };
      });
      setSelectedGroupScenarios(groupScenarios);
      setSelectedItems([{ id: `group-${groupKey}`, group: groupKey, label: groupKey }]);
      setGroupCheckboxes((prev) => ({ ...prev, [groupKey]: "checked" }));
      return;
    }

    if (mode === "Advanced") {
      const updated = (groupObj.scenarios || []).map((s) => ({
        ...s,
        selected: false,
        count: s.count || 1,
        durationHours: s.durationHours || 1,
      }));

      // hydrate from right pane if present
      const tagsForGroup = selectedItems.filter(
        (t) => t.group === groupKey && !t.id.startsWith("group-")
      );
      if (tagsForGroup.length) {
        const mapCounts = Object.fromEntries(
          tagsForGroup.map((t) => {
            const parts = String(t.label).split(" - ");
            const parsed = parseInt(parts[parts.length - 1], 10) || 1;
            return [t.id, parsed];
          })
        );

        updated.forEach((s) => {
          if (mapCounts[s.id]) {
            s.selected = true;

            if (isDurationScenario(s)) {
              s.durationHours = mapCounts[s.id];
            } else {
              s.count = mapCounts[s.id];
            }
          }
        });
      }

      setSelectedGroupScenarios(updated);
      const tags = updated
        .filter((s) => s.selected)
        .map((s) => ({ id: s.id, group: groupKey, label: buildScenarioLabel(s) }));
      setSelectedItems((prev) => [
        ...prev.filter((it) => it.group !== groupKey),
        ...tags,
      ]);

      const total = updated.length;
      const selectedCount = updated.filter((s) => s.selected).length;
      setGroupCheckboxes((prev) => ({
        ...prev,
        [groupKey]:
          selectedCount === 0
            ? "none"
            : selectedCount === total
              ? "checked"
              : "partial",
      }));
      return;
    }

    if (mode === "Reset") {
      setSelectedGroupScenarios([]);

      const cleanupTitles = findGroupCleanups(groupKey);
      const cleanupTags = cleanupTitles.map((title) => {
        const scenario = (groupObj.scenarios || []).find((s) => s.title === title);
        return {
          id: scenario?.id || title,
          type: "scenario",
          group: groupKey,
          label: `${title} - 1`,
        };
      });

      setSelectedItems(
        cleanupTags.length
          ? cleanupTags
          : [{ id: `reset-${groupKey}`, type: "reset", group: groupKey, label: `${groupKey} Cleanup` }]
      );
      setGroupCheckboxes((prev) => ({ ...prev, [groupKey]: "none" }));
    }
  };

  /** ---------- middle pane: per-scenario interactions ---------- */
  const handleScenarioToggle = (idx) => {
    if (getCurrentMode() === "Reset") return;
    const updated = [...selectedGroupScenarios];
    const s = updated[idx];
    s.selected = !s.selected;
    setSelectedGroupScenarios(updated);

    if (getCurrentMode() === "Advanced") {
      const tags = updated
        .filter((x) => x.selected)
        .map((x) => ({ id: x.id, group: selectedGroup, label: buildScenarioLabel(x) }));
      setSelectedItems((prev) => [
        ...prev.filter((it) => it.group !== selectedGroup),
        ...tags,
      ]);
    }

    const total = updated.length;
    const selectedCount = updated.filter((x) => x.selected).length;
    setGroupCheckboxes((prev) => ({
      ...prev,
      [selectedGroup]:
        selectedCount === 0 ? "none" : selectedCount === total ? "checked" : "partial",
    }));
  };

  const handleSelectAllScenarios = () => {
    const allSelected = selectedGroupScenarios.every((s) => s.selected);
    const nextSelected = !allSelected;
    const updated = selectedGroupScenarios.map((s) => ({ ...s, selected: nextSelected }));
    setSelectedGroupScenarios(updated);

    setGroupCheckboxes((prev) => ({ ...prev, [selectedGroup]: nextSelected ? "checked" : "none" }));

    if (getCurrentMode() === "Advanced") {
      const tags = nextSelected
        ? updated.map((s) => ({ id: s.id, group: selectedGroup, label: buildScenarioLabel(s) }))
        : [];
      setSelectedItems((prev) => [
        ...prev.filter((it) => it.group !== selectedGroup),
        ...tags,
      ]);
    }
  };

  const updateScenarioCount = (idx, newVal) => {
    const updated = [...selectedGroupScenarios];
    if (isInitScenario(updated[idx])) return;

    const current = updated[idx];

    if (isDurationScenario(current)) {
      updated[idx].durationHours = newVal;
    } else {
      updated[idx].count = newVal;
    }

    setSelectedGroupScenarios(updated);
  };

  const sanitizeScenarioCount = (idx, raw) => {
    let value = parseInt(raw, 10);
    if (isNaN(value) || value <= 0) value = 1;

    const updated = [...selectedGroupScenarios];
    if (isInitScenario(updated[idx])) return;

    const current = updated[idx];

    if (isDurationScenario(current)) {
      updated[idx].durationHours = value;
    } else {
      updated[idx].count = value;
    }

    setSelectedGroupScenarios(updated);

    const s = updated[idx];
    setSelectedItems((prev) =>
      prev.map((it) =>
        it.id === s.id
          ? { ...it, group: selectedGroup, label: buildScenarioLabel(s) }
          : it
      )
    );
  };

  /** ---------- right pane ---------- */
  const handleRemoveSelectedItem = (idToRemove) => {
    const isGroupTag = idToRemove.startsWith("group-");
    const groupKey = isGroupTag ? idToRemove.replace("group-", "") : null;

    if (isGroupTag) {
      setSelectedItems((prev) => prev.filter((it) => it.group !== groupKey));
      if (selectedGroup === groupKey) {
        const updated = selectedGroupScenarios.map((s) => ({ ...s, selected: false }));
        setSelectedGroupScenarios(updated);
        setGroupCheckboxes((prev) => ({ ...prev, [groupKey]: "none" }));
      }
      return;
    }

    setSelectedItems((prev) => prev.filter((it) => it.id !== idToRemove));
    setSelectedGroupScenarios((prev) => {
      const updated = prev.map((s) => (s.id === idToRemove ? { ...s, selected: false } : s));
      const total = updated.length;
      const selectedCount = updated.filter((s) => s.selected).length;
      setGroupCheckboxes((p) => ({
        ...p,
        [selectedGroup]:
          selectedCount === 0 ? "none" : selectedCount === total ? "checked" : "partial",
      }));
      return updated;
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(selectedItems);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setSelectedItems(items);
  };


  const isoUtcToTimeInTz = (isoUtc, tz) => {
    if (!isoUtc) return null;
    try {
      const d = new Date(isoUtc); // already has 'Z'
      if (Number.isNaN(d.getTime())) return null;

      const parts = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: tz,
      }).formatToParts(d);

      const hh = parts.find(p => p.type === "hour")?.value ?? "00";
      const mm = parts.find(p => p.type === "minute")?.value ?? "00";
      return `${hh}:${mm}`;
    } catch {
      return null;
    }
  };

  const getUiTimeAndTzFromRecord = (record, viewerTz = "UTC") => {
    if (!record) {
      return { time24: "09:00", scheduleTz: viewerTz || "UTC" };
    }

    // 1) Prefer enriched values from parent
    const scheduleTz =
      record.uiTimezone ||
      record.scheduleCriteria?.timezone ||
      viewerTz ||
      "UTC";

    if (record.uiTime24) {
      return { time24: record.uiTime24, scheduleTz };
    }

    // 2) New backend field: scheduledTime is an ISO string in UTC
    if (typeof record.scheduledTime === "string") {
      const t = isoUtcToTimeInTz(record.scheduledTime, scheduleTz);
      if (t) return { time24: t, scheduleTz };
    }

    // 3) Legacy: scheduleCriteria.time is stored as local time string
    if (record.scheduleCriteria?.time) {
      return { time24: record.scheduleCriteria.time, scheduleTz };
    }

    // Fallback
    return { time24: "09:00", scheduleTz };
  };



  const DAY_CODE_TO_FULL = {
    MO: "Monday",
    TU: "Tuesday",
    WE: "Wednesday",
    TH: "Thursday",
    FR: "Friday",
    SA: "Saturday",
    SU: "Sunday",
  };
  const DAY_FULL_TO_CODE = Object.fromEntries(
    Object.entries(DAY_CODE_TO_FULL).map(([k, v]) => [v, k])
  );

  /** ---------- hydrate from record ---------- */
  useEffect(() => {
    if (!isOpen) return;

    // tenant
    const t = (tenantOptions || []).find((x) => x.tenantUrl === record?.tenantUrl);
    setTenantForm({
      tenantUrl: t?.tenantUrl || record?.tenantUrl || "",
      itsmUrl: t?.itsmUrl || "",
      key: t?.key || "",
      secretKey: t?.secretKey || "",
      username: t?.username || "",
      password: t?.password || "",
    });

    // ---- TIME / TZ (now handles scheduledTime ISO) ----
    const { time24, scheduleTz } = getUiTimeAndTzFromRecord(record, systemTimezone);

    const [h24Str, mmStr] = String(time24).split(":");
    const h24 = parseInt(h24Str || "9", 10);
    const mm = pad2(parseInt(mmStr || "0", 10));
    const ap = h24 >= 12 ? "PM" : "AM";
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;

    setHourStr(pad2(h12));
    setMinuteStr(mm);
    setAmpm(ap);
    setTimezone(scheduleTz);


    // ---- DAYS (prefer daysOfWeek from recurringRows) ----
    const apiDaysFull =
      Array.isArray(record?.frequency?.days)
        ? record.frequency.days
        : Array.isArray(record?.scheduleCriteria?.days)
          ? record.scheduleCriteria.days
          : Array.isArray(record?.daysOfWeek)
            ? record.daysOfWeek
              .map((c) => DAY_CODE_TO_FULL[c])
              .filter(Boolean)
            : [];

    const codesFromRow =
      Array.isArray(record?.daysOfWeek) && record.daysOfWeek.length
        ? record.daysOfWeek
        : apiDaysFull
          .map((d) => DAY_FULL_TO_CODE[d] || null)
          .filter(Boolean);

    setSelectedDayCodes(codesFromRow);

    // ---- ENABLED ----
    setEnabled(
      typeof record?.scheduleCriteria?.enabled === "boolean"
        ? record.scheduleCriteria.enabled
        : typeof record?.enabled === "boolean"
          ? record.enabled
          : true
    );

    const tzForEnd = scheduleTz || systemTimezone;

    if (record?.endDate) {
      const localDateStr = dateInputFromUtc(record.endDate, tzForEnd);
      setEndDateStr(localDateStr);
      setNoEnd(false);
    } else {
      setEndDateStr("");
      setNoEnd(true);
    }



    // RIGHT PANE TAGS from preselectByGroup
    // (so it exactly mirrors saved selection)
    const allTags = [];
    Object.entries(preselectByGroup).forEach(([gk, cfg]) => {
      if (cfg.mode === "Default") {
        allTags.push({ id: `group-${gk}`, group: gk, label: gk });
        return;
      }
      if (cfg.mode === "Reset") {
        const cleanupTitles = findGroupCleanups(gk);
        const cleanupTags = cleanupTitles.map((title) => {
          const s = (scenarioGroups.find(x => x.title === gk)?.scenarios || []).find(z => z.title === title);
          return { id: s?.id || title, type: "scenario", group: gk, label: `${title} - 1` };
        });
        if (cleanupTags.length) allTags.push(...cleanupTags);
        else allTags.push({ id: `reset-${gk}`, type: "reset", group: gk, label: `${gk} Cleanup` });
        return;
      }
      // Advanced
      cfg.items.forEach((it) => {
        const s = (scenarioGroups || [])
          .flatMap((g) => g.scenarios || [])
          .find((x) => x.id === it.id || x.title === it.title);

        const title = s?.title || it.title;

        allTags.push({
          id: s?.id || it.id || it.title,
          group: gk,
          label:
            s && isDurationScenario(s)
              ? `${title} - ${it.count || 1} hour${(it.count || 1) > 1 ? "s" : ""}`
              : `${title} - ${it.count || 1}`,
        });
      });
    });
    setSelectedItems(allTags);

    // open a meaningful group (first from record or first in map)
    const firstTitle = record?.selectedScenarios?.[0]?.title;
    const firstGroup =
      firstTitle ? titleToGroup(firstTitle, scenarioGroups)
        : Object.keys(preselectByGroup)[0] || scenarioGroups[0]?.title || null;

    if (firstGroup) {
      const groupObj = scenarioGroups.find((g) => g.title === firstGroup);
      if (groupObj) restoreGroupScenarios(groupObj);
    }

    // if backend sends this flag, keep it
    if (typeof record.resetRticCluster === "boolean") {
      setResetRticCluster(record.resetRticCluster);
    } else {
      setResetRticCluster(null); // unset
    }
    setRticTouched(false);
  }, [isOpen, record, tenantOptions, scenarioGroups, preselectByGroup]);


  // keep the select-all checkbox indeterminate state
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = getAdvancedSelectionState() === "some";
    }
  }, [selectedGroup, selectedGroupScenarios, scenarioModes]);






  // compact pills like your modal
  const DAY_OPTS = [
    { code: "MO", label: "M" },
    { code: "TU", label: "T" },
    { code: "WE", label: "W" },
    { code: "TH", label: "T" },
    { code: "FR", label: "F" },
    { code: "SA", label: "S" },
    { code: "SU", label: "S" },
  ];


  // was: const [weekDays, setWeekDays] = useState([]);
  const [selectedDayCodes, setSelectedDayCodes] = useState([]);

  // toggle like your modal (select/deselect)
  const toggleDay = (code) => {
    setSelectedDayCodes(prev => {
      const next = prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code];
      // keep a stable order MO..SU
      const order = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
      return order.filter(c => next.includes(c));
    });
  };


  /** ---------- SAVE (build payload; leave dispatch for later) ---------- */
  const handleSave = async () => {
    // scenarios
    const scenariosPayload = selectedItemsToScenariosPayload(selectedItems, scenarioGroups);
    if (!scenariosPayload.length) {
      Swal.fire("No scenarios", "Please select at least one scenario or group.", "warning");
      return;
    }

    // time HH:MM (24h)
    if (!hourStr || !minuteStr || !ampm) {
      Swal.fire("Missing time", "Please select start time.", "warning");
      return;
    }
    const time24 = `${pad2(to24h(hourStr, ampm))}:${pad2(parseInt(minuteStr, 10) || 0)}`;

    // tenant (basic presence)
    if (!tenantForm?.tenantUrl) {
      Swal.fire("Missing tenant", "Tenant URL is required.", "warning");
      return;
    }

    // day codes → full names
    const DAY_CODE_TO_FULL = {
      MO: "Monday", TU: "Tuesday", WE: "Wednesday",
      TH: "Thursday", FR: "Friday", SA: "Saturday", SU: "Sunday",
    };
    const daysFull = selectedDayCodes.map(c => DAY_CODE_TO_FULL[c]).filter(Boolean);
    if (!daysFull.length) {
      Swal.fire("No days selected", "Please choose at least one weekday.", "warning");
      return;
    }

    const isResetMode = getCurrentMode() === "Reset";

    // build payload
    const payload = {
      tenantDetails: {
        tenantUrl: tenantForm.tenantUrl,
        itsmUrl: tenantForm.itsmUrl,
        key: tenantForm.key,
        secretKey: tenantForm.secretKey,
        username: tenantForm.username,
        password: tenantForm.password,
      },
      selectedScenarios: scenariosPayload,
      scheduleCriteria: {
        type: "weekly",
        days: daysFull,
        time: time24,
        timezone: timezone || systemTimezone,
        enabled: !!enabled,
      },
      endDate: noEnd ? null : (endDateStr || null),
    };

    // ✅ RTIC belongs ONLY to Reset mode
    if (isResetMode) {
      payload.resetRticCluster = !!resetRticCluster;
    }

    setIsSaving(true);
    try {
      const taskId = record?.scheduleId || record?.taskId;
      console.log("PATCH Recurring Edit ➜", { taskId, payload });

      await dispatch(editYadsRecurringSchedule(taskId, payload));
      dispatch(getYadsPollingStatus());
      // Success/error alerts handled by watcher useEffect
    } catch (e) {
      await Swal.fire({
        icon: "error",
        title: "Update failed",
        text: e?.message || "Something went wrong.",
        confirmButtonColor: "#05264e",
      });
    } finally {
      setIsSaving(false);
    }
  };


  /** ---------- close on ESC ---------- */
  const onKey = useCallback(
    (e) => {
      if (e.key === "Escape" && isOpen) onClose?.();
    },
    [isOpen, onClose]
  );
  useEffect(() => {
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onKey]);

  if (!isOpen) return null;

  const groupDesc =
    scenarioGroups?.find(g => g.title === selectedGroup)?.description || "";

  /** ---------- render ---------- */
  return (
    <>
      {isSaving && <LoadingScreen />}
      <div
        className="yads-edit-overlay"
        onClick={(e) => {
          if (e.target.classList.contains("yads-edit-overlay")) onClose?.();
        }}
      >
        <div className="yads-edit-modal wide" role="dialog" aria-modal="true">
          <div className="yads-edit-header">
            <h4 className="yads-edit-title">Edit Recurring Ingestion</h4>
          </div>

          <div className="yads-edit-body">
            <div className="yads-container" style={{ paddingTop: 0, paddingBottom: 0 }}>
              {/* Left: groups */}
              <div className="panel scenario-groups">
                <h4 className="section-title-yads">Scenario Groups</h4>
                <div className="scenario-group-list">
                  {(scenarioGroups || []).map((group) => (
                    <div
                      key={group.title}
                      className={`group-item ${selectedGroup === group.title ? "selected" : ""}`}
                      onClick={() => {
                        if (selectedGroup !== group.title) restoreGroupScenarios(group);
                      }}
                    >
                      <span>{group.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Middle: scenarios */}
              <div className={`panel scenario-details ${!selectedGroup ? "blurred" : ""}`}>
                {!selectedGroup && (
                  <div className="blur-overlay">
                    <p>Please select a Scenario Group to continue</p>
                  </div>
                )}

                <div className="scenario-header">
                  <h5 className="group-title">{selectedGroup || "—"}</h5>
                </div>

                <div
                  className="group-description"
                  dangerouslySetInnerHTML={{
                    __html: groupDesc || "No description available.",
                  }}
                />

                {selectedGroup && (
                  <>
                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
                      <div className="scenario-mode-tabs">
                        <span
                          className={`tab ${getCurrentMode() === "Default" ? "active" : ""}`}
                          onClick={() => handleScenarioModeChange("Default")}
                        >
                          Default
                        </span>
                        <span
                          className={`tab ${getCurrentMode() === "Advanced" ? "active" : ""}`}
                          onClick={() => handleScenarioModeChange("Advanced")}
                        >
                          Advanced
                        </span>
                        <span
                          className={`tab ${getCurrentMode() === "Reset" ? "active" : ""}`}
                          onClick={() => handleScenarioModeChange("Reset")}
                        >
                          Reset
                        </span>
                      </div>
                    </div>
                    <br />

                    {getCurrentMode() === "Advanced" && selectedGroupScenarios.length > 0 && (
                      <div className="scenario-item select-all-row">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={getAdvancedSelectionState() === "all"}
                          onChange={handleSelectAllScenarios}
                        />
                        <div className="scenario-info">
                          <span className="scenario-title">Select all in {selectedGroup}</span>
                        </div>
                      </div>
                    )}

                    {getCurrentMode() !== "Default" &&
                      selectedGroupScenarios.length > 0 &&
                      selectedGroupScenarios.map((scenario, index) => {
                        const isInit = isInitScenario(scenario);
                        return (
                          <div className="scenario-item" key={scenario.id}>
                            <input
                              type="checkbox"
                              checked={scenario.selected}
                              onChange={() => handleScenarioToggle(index)}
                            />
                            <div className="scenario-info">
                              <span className="scenario-title">
                                {scenario.title}
                                {isInit && " (required)"}
                              </span>
                              {!!scenario.description && (
                                <p className="scenario-desc">{scenario.description}</p>
                              )}
                            </div>
                            <div className="scenario-metric">
                              <input
                                type="number"
                                className="scenario-count"
                                value={isDurationScenario(scenario) ? scenario.durationHours : scenario.count}
                                min="1"
                                onChange={(e) => updateScenarioCount(index, e.target.value)}
                                onBlur={(e) => sanitizeScenarioCount(index, e.target.value)}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                                }}
                                disabled={isInit}
                                title={
                                  isInit
                                    ? "Count locked for Init"
                                    : isDurationScenario(scenario)
                                      ? "Enter duration in hours"
                                      : "Enter number of runs"
                                }
                              />
                              <span className="scenario-unit-hint">
                                {isDurationScenario(scenario) ? "hours" : "runs"}
                              </span>
                            </div>
                          </div>
                        );
                      })}

                    {getCurrentMode() === "Reset" && (
                      <div className="reset-message">
                        <br />
                        <p>⚠️ You are in Reset mode. Only cleanup scenarios will be added to reset ingestion.</p>

                        <label className="rtic-reset-option">
                          <input
                            type="checkbox"
                            checked={!!resetRticCluster}
                            onChange={(e) => {
                              setResetRticCluster(e.target.checked);
                              setRticTouched(true);
                            }}
                          />
                          <span>RTIC Cluster Reset</span>
                        </label>


                        <div className="rtic-warning">
                          <div className="rtic-warning__icon">⚠️</div>
                          <div className="rtic-warning__text">
                            Enabling this will reset the RTIC cluster across all services. Incidents will be cleared from the cluster but will not be resolved. The service-level reset will resolve incidents for this service.
                          </div>
                        </div>

                      </div>
                    )}

                    {getCurrentMode() === "Default" && selectedGroupScenarios.length > 0 && (
                      <div className="default-scenario-list">
                        {selectedGroupScenarios.map((scenario) => (
                          <div className="scenario-readonly" key={scenario.id}>
                            <div className="readonly-header">
                              <span className="scenario-title">{scenario.title}</span>
                              <span className="scenario-count-readonly">
                                {isDurationScenario(scenario)
                                  ? `${getScenarioValue(scenario)} hour${getScenarioValue(scenario) > 1 ? "s" : ""}`
                                  : `x${getScenarioValue(scenario)}`}
                              </span>
                            </div>
                            {!!scenario.description && (
                              <div className="scenario-desc">{scenario.description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Right: selected items + tenant + recurring schedule */}
              <div className={`panel ingestion-config ${selectedItems.length === 0 ? "blurred" : ""}`}>
                {selectedItems.length === 0 && (
                  <div className="blur-overlay">
                    <p>Please select at least one Scenario or Group</p>
                  </div>
                )}

                {/* Selected items */}
                <div className="section-header">
                  <h6>Selected Items</h6>
                  <hr />
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="selected-tags" direction="vertical">
                    {(provided) => (
                      <div className="selected-tags" {...provided.droppableProps} ref={provided.innerRef}>
                        {selectedItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided) => (
                              <span
                                className="tag ordered-tag"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <span className="order-index">{index + 1}.</span>
                                {item.label}
                                <button onClick={() => handleRemoveSelectedItem(item.id)}>×</button>
                              </span>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {/* Tenant */}
                <div className="section-header" style={{ marginTop: 8 }}>
                  <h6>Tenant</h6>
                  <hr />
                </div>

                <div className="new-tenant-form">
                  <label htmlFor="tenant-url">Tenant URL</label>
                  <input
                    id="tenant-url"
                    type="text"
                    className="yads-edit-input"
                    value={tenantForm.tenantUrl}
                    onChange={(e) => setTenantForm((f) => ({ ...f, tenantUrl: e.target.value }))}
                    placeholder="https://{tenant}-itom-demo.onbmc.com"
                  />

                  <label htmlFor="itsm-url">ITSM URL</label>
                  <input
                    id="itsm-url"
                    type="text"
                    className="yads-edit-input"
                    value={tenantForm.itsmUrl}
                    onChange={(e) => setTenantForm((f) => ({ ...f, itsmUrl: e.target.value }))}
                    placeholder="https://{tenant}-demo-restapi.onbmc.com"
                  />

                  <div className="row">
                    <div className="field">
                      <label htmlFor="access-key">ITOM Access Key</label>
                      <input
                        id="access-key"
                        type="text"
                        value={tenantForm.key}
                        onChange={(e) => setTenantForm((f) => ({ ...f, key: e.target.value }))}
                      />
                    </div>

                    <div className="field">
                      <label htmlFor="secret-key">ITOM Secret Key</label>
                      <input
                        id="secret-key"
                        type="password"
                        value={tenantForm.secretKey}
                        onChange={(e) => setTenantForm((f) => ({ ...f, secretKey: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="field">
                      <label htmlFor="itsm-username">ITSM Username</label>
                      <input
                        id="itsm-username"
                        type="text"
                        value={tenantForm.username}
                        onChange={(e) => setTenantForm((f) => ({ ...f, username: e.target.value }))}
                      />
                    </div>

                    <div className="field">
                      <label htmlFor="itsm-password">ITSM Password</label>
                      <input
                        id="itsm-password"
                        type="password"
                        value={tenantForm.password}
                        onChange={(e) => setTenantForm((f) => ({ ...f, password: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Recurring schedule */}
                <div className="section-header" style={{ marginTop: 8 }}>
                  <h6>Recurring Schedule</h6>
                  <hr />
                </div>

                <div className="ohom">
                  {/* Weekly days */}
                  <div className="sched-input" style={{ width: "100%" }}>
                    <span className="sched-input__label">Weekly Days</span>
                    <div className="days-pills days-pills--compact">
                      {DAY_OPTS.map(({ code, label }) => {
                        const active = selectedDayCodes.includes(code);
                        return (
                          <button
                            type="button"
                            key={code}
                            className={`day-pill day-pill--compact ${active ? "is-active" : ""}`}
                            onClick={() => toggleDay(code)}
                            aria-pressed={active}
                            title={DAY_CODE_TO_FULL[code]}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <br></br>

                  {/* Start time */}
                  <label className="sched-input">
                    <span className="sched-input__label">Start Time</span>
                    <div className="time-group" role="group" aria-label="Select time">
                      <div className="time-seg">
                        <select
                          className="time-seg__select"
                          aria-label="Hour"
                          value={hourStr}
                          onChange={(e) => setHourStr(e.target.value)}
                          required
                        >
                          <option value="">HH</option>
                          {HOURS_12.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                        <span className="time-seg__caret">▾</span>
                      </div>

                      <span className="time-sep">:</span>

                      <div className="time-seg">
                        <select
                          className="time-seg__select"
                          aria-label="Minute"
                          value={minuteStr}
                          onChange={(e) => setMinuteStr(e.target.value)}
                          required
                        >
                          <option value="">MM</option>
                          {MINUTES.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <span className="time-seg__caret">▾</span>
                      </div>

                      <div className="time-seg time-seg--ampm">
                        <select
                          className="time-seg__select"
                          aria-label="AM or PM"
                          value={ampm}
                          onChange={(e) => setAmpm(e.target.value)}
                          required
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                        <span className="time-seg__caret">▾</span>
                      </div>
                    </div>
                  </label>
                  <br></br>


                  <div className="row-inline">
                    {/* End date + No End toggle on one row */}
                    <div className="sched-input grow">
                      <span className="sched-input__label">End</span>
                      <div className="inline-controls">
                        <div className="sched-input__wrap">
                          <input
                            type="date"
                            className="sched-input__control"
                            value={endDateStr}
                            onChange={(e) => setEndDateStr(e.target.value)}
                            disabled={noEnd}
                          />
                          <span className="sched-input__caret">▾</span>
                        </div>

                        <Switch
                          id="no-end-switch"
                          checked={noEnd}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setNoEnd(v);
                            if (v) setEndDateStr("");
                          }}
                          label="No end"
                        />
                      </div>
                    </div>

                    {/* Enabled toggle on the same row, right side */}
                    <div className="sched-input">
                      <span className="sched-input__label">Status</span>
                      <div className="inline-controls">
                        <Switch
                          id="enabled-switch"
                          checked={enabled}
                          onChange={(e) => setEnabled(e.target.checked)}
                          label={enabled ? "Enabled" : "Disabled"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>




          <div className="yads-edit-footer">
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave}>Update</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Yads_Recurring_Scheduled_Edit;
